import { motion } from "framer-motion";
import { IMAGES } from "../../data/content";
import { fileSrc } from "../builder/builderUi";
import { useGivingSummary, formatDollars } from "../../lib/recurringGifts";
import { StatusStamp } from "./StatusStamp";

/**
 * DashboardPlate — "your file": a beige band textured with the brand pattern
 * holding a bone folder. The colour logo is a sticker on its corner, the
 * student's photo (or a tangerine name tile) sits beside their name, the
 * campaign state is a passport stamp, and the account facts run as one
 * ledger line. Donors get the same plate without the stamp.
 */
const EASE = [0.22, 1, 0.36, 1];

const PAYOUTS = {
  not_started: "Payouts not set up yet",
  in_progress: "Payout setup in progress",
  needs_info: "Stripe needs a few more details",
  in_review: "Payouts in review at Stripe",
  ready: "Payouts ready",
};

const enrollmentLine = (user, draft) => {
  if (user.enrollment_verified || draft?.enrollment_verified) return "Enrollment verified by our team";
  if (draft?.status === "pending_review") return "Enrollment is verified when your campaign is approved";
  return "Enrollment is verified as part of your campaign review";
};

const monthYear = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "");

/* Donor ledger: monthly gift · giving since · receipts. */
const givingLines = (g) => {
  if (!g) return [];
  return [
    g.monthly_cents ? `${formatDollars(g.monthly_cents)} a month to the General Fund` : "No monthly gift yet",
    g.first_gift_at ? `Giving since ${monthYear(g.first_gift_at)}` : "Your first gift is still ahead",
    g.receipts ? `${g.receipts} ${g.receipts === 1 ? "receipt" : "receipts"}, all in your inbox` : "Receipts arrive by email after every gift",
  ];
};

const Sticker = () => (
  <motion.img
    src={IMAGES.logoColor}
    alt="My Rich Aunt"
    initial={{ opacity: 0, rotate: 18, scale: 1.12, y: -10 }}
    animate={{ opacity: 1, rotate: [18, 4, 10, 7, 8], scale: [1.12, 0.97, 1.03, 0.99, 1], y: 0 }}
    transition={{ duration: 0.9, delay: 0.25, ease: "easeOut", times: [0, 0.45, 0.65, 0.85, 1] }}
    whileHover={{ rotate: 4, scale: 1.03 }}
    className="absolute -right-3 -top-8 h-24 w-24 rounded-2xl shadow-[0_14px_30px_-12px_rgba(0,0,0,0.55)] ring-1 ring-ink/10 sm:-right-5 sm:-top-9 sm:h-28 sm:w-28 lg:-right-7 lg:-top-10 lg:h-32 lg:w-32"
    data-testid="dashboard-logo-sticker"
  />
);

export const DashboardPlate = ({ user, draft, isStudent, giving: givingProp }) => {
  const fetched = useGivingSummary({ auto: !isStudent && givingProp === undefined });
  const giving = givingProp === undefined ? fetched : givingProp;
  const name = user.display_name || user.email?.split("@")[0] || "";
  const first = name.split(" ")[0];
  const tileSize = first.length <= 4 ? "text-[3.5rem]" : "text-3xl";
  const photo = draft?.cover_file_id ? fileSrc(draft.cover_file_id) : null;
  const deck = isStudent ? [user.school, "Student"].filter(Boolean).join(" · ") : "Donor";
  const brief = isStudent
    ? "Your campaign lives here. Build it once, submit it once, and keep it fresh after it goes live."
    : "Your giving lives here: recurring gifts, receipts, and the students you've backed.";

  return (
    <section className="bg-sand pt-28 lg:pt-32" data-testid="dashboard-plate">
      <div className="relative bg-beige pb-12 pt-14 lg:pb-16 lg:pt-20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: `url(${IMAGES.pattern})`, backgroundSize: "420px", backgroundRepeat: "repeat", maskImage: "linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0.15))", WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0.15))" }}
        />
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: EASE }} className="relative mx-auto max-w-5xl px-6 lg:px-8">
          <div className="relative rounded-2xl bg-bone p-7 pt-14 shadow-[0_24px_60px_-32px_rgba(26,23,20,0.35)] sm:pt-7 lg:p-10">
            <Sticker />
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
              <div className={`flex flex-col gap-6 sm:flex-row sm:items-start ${isStudent && draft ? "lg:col-span-7" : "lg:col-span-12"}`}>
                <div className="h-28 w-28 shrink-0 overflow-hidden rounded-xl bg-tangerine lg:h-32 lg:w-32" data-testid="dashboard-name-tile">
                  {photo ? (
                    <img src={photo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className={`flex h-full w-full items-center justify-center px-2 font-serif italic leading-none text-ink ${tileSize}`}>{first}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="font-serif text-3xl lg:text-[3.5rem] leading-none tracking-tight text-ink" data-testid="dashboard-name">{name}</h1>
                  <p className="mt-2 whitespace-nowrap font-serif text-3xl text-olive" data-testid="dashboard-deck">{deck}</p>
                  <p className="mt-4 max-w-md text-base leading-relaxed text-olive">{brief}</p>
                </div>
              </div>
              {isStudent && draft && (
                <div className="lg:col-span-5 lg:pt-12">
                  <StatusStamp draft={draft} />
                </div>
              )}
            </div>
            <ul className="mt-9 flex flex-col gap-2 border-t border-ink/10 pt-5 text-base leading-snug text-ink sm:flex-row sm:flex-wrap sm:gap-0" data-testid="dashboard-ledger">
              <li className="sm:pr-5" data-testid="dashboard-user-email">{user.email}</li>
              {isStudent ? (
                <>
                  <li className="text-olive sm:border-l sm:border-ink/15 sm:px-5" data-testid="dashboard-enrollment-status">{enrollmentLine(user, draft)}</li>
                  {draft && <li className="text-olive sm:border-l sm:border-ink/15 sm:pl-5" data-testid="dashboard-payout-status">{PAYOUTS[draft.payout_state] || PAYOUTS.not_started}</li>}
                </>
              ) : (
                givingLines(giving).map((line, i) => (
                  <li key={line} className="text-olive sm:border-l sm:border-ink/15 sm:px-5 sm:last:pr-0" data-testid={`dashboard-giving-${["monthly", "since", "receipts"][i]}`}>{line}</li>
                ))
              )}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
